import { ExtendedAccountColumns, ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { accountsLinks } from '@/backend/routes';
import { AdditionAccountHeaderContent } from '@/ui/features/accounts';
import { fixOccupationProfessionalStatusCredentialLicenses } from '@/ui/features/accounts/RmsAccountDetails';
import { SlideoutMenuItem } from '@/ui/hooks';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { PartialError } from '@juvare/common-web-ui/PartialError';
import { accountEntityName, getAccountPermissions } from '@juvare/contact-management/backend/features/accounts/shared';
import { AccountDetails, AccountDetailsProps } from '@juvare/contact-management/ui/features/accounts/components/AccountDetails';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { OccupationInformation, PersonSubtitle } from '@juvare/credentialing/ui/features/person-credentials';
import { useProfessionalStatuses } from '@juvare/credentialing/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useStorageGet } from '@juvare/platform-sdk-react/ui/hooks';
import { CircularProgress } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface RmsAccountDetailsComponentProps {
    slideoutMenuItems: SlideoutMenuItem[];
    onClose: () => void;
    accountId?: string;
}

export const RmsAccountDetailsComponent = ({ accountId, slideoutMenuItems, onClose }: RmsAccountDetailsComponentProps) => {
    const navigate = useNavigate();
    const userState = useUserState();
    const settings = useSelector(getSettings);
    const { t } = useTranslation();
    const { professionalStatusCredentialId } = useProfessionalStatuses();
    const navigationContext = React.useContext(NavigationContext);
    const { data: account, isLoading, error } = useStorageGet<ExtendedAccountData, ExtendedAccountColumns>(accountEntityName, accountId);
    if (isLoading || !account || !accountId) {
        return null;
    }
    const accountPermissions = getAccountPermissions(settings, userState, accountId, account.data);

    const handleNavigateToAccountPage = () => {
        navigationContext.navigate(() => {
            navigate(`${accountsLinks.root}/${accountId}`, { state: { back: true } });
        });
    };

    const detailsConfig: AccountDetailsProps = {
        reloadOktaStatus: () => {},
        account,
        accountPermissions,
        completionBarTitle: t('common.profileCompletion'),
        subTitle: (
            <PersonSubtitle
                tribeId={account.data.address?.tribeId}
                county={account.data.address?.county}
                residenceType={account.data.address?.residenceType}
            />
        ),
        additionalContent: (
            <OccupationInformation
                personData={{
                    ...account.data,
                    occupations: fixOccupationProfessionalStatusCredentialLicenses(
                        account.data.occupations,
                        professionalStatusCredentialId,
                    ),
                }}
            />
        ),
        onViewProfile: handleNavigateToAccountPage,
        additionalHeaderContent: <AdditionAccountHeaderContent account={account.data} cardStyle='slideout' />,
        onClose,
        menuItems: slideoutMenuItems ?? [],
    };
    return (
        <>
            {account && !error && <AccountDetails {...detailsConfig} />}
            {isLoading && !account && <CircularProgress />}
            {!account && error && <PartialError error={error} />}
        </>
    );
};
